import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutlined as CheckCircleIcon,
  LockOutlined as LockIcon,
  SendOutlined as SendIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../api/api";
import { persistor } from "../redux/store";
import { logoutUser } from "../redux/userReducer";

function getErrorMessage(error, fallbackMessage) {
  const data = error?.response?.data;
  const validationErrors = data?.errors;

  if (validationErrors && typeof validationErrors === "object") {
    const firstError = Object.values(validationErrors).find(
      (messages) => Array.isArray(messages) && messages.length > 0,
    );

    if (firstError?.[0]) {
      return firstError[0];
    }
  }

  return data?.message || error?.message || fallbackMessage;
}

function getOptions(field) {
  return Array.isArray(field?.Settings?.options)
    ? field.Settings.options
    : [];
}

function buildInitialValues(fields) {
  return fields.reduce((values, field) => {
    if (field.FieldType !== "heading") {
      values[field.FieldKey] = field.FieldType === "checkbox" ? [] : "";
    }

    return values;
  }, {});
}

function DynamicFormField({ field, value, onChange }) {
  const commonProps = {
    fullWidth: true,
    label: field.Label,
    required: field.IsRequired,
    helperText: field.HelpText || " ",
    value: value ?? "",
    onChange: (event) => onChange(event.target.value),
  };

  if (field.FieldType === "heading") {
    return (
      <Box sx={{ pt: 1 }}>
        <Typography variant="h5" fontWeight={800} color="#0f172a">
          {field.Label}
        </Typography>
        {field.HelpText ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {field.HelpText}
          </Typography>
        ) : null}
      </Box>
    );
  }

  if (field.FieldType === "long_text") {
    return (
      <TextField
        {...commonProps}
        multiline
        minRows={4}
        placeholder={field.Placeholder || ""}
      />
    );
  }

  if (["dropdown"].includes(field.FieldType)) {
    return (
      <TextField {...commonProps} select>
        {getOptions(field).map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (field.FieldType === "radio") {
    return (
      <FormControl required={field.IsRequired}>
        <FormLabel>{field.Label}</FormLabel>
        <RadioGroup
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
        >
          {getOptions(field).map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              control={<Radio />}
              label={option}
            />
          ))}
        </RadioGroup>
        {field.HelpText ? (
          <Typography variant="caption" color="text.secondary">
            {field.HelpText}
          </Typography>
        ) : null}
      </FormControl>
    );
  }

  if (field.FieldType === "checkbox") {
    const selectedValues = Array.isArray(value) ? value : [];

    return (
      <FormControl required={field.IsRequired}>
        <FormLabel>{field.Label}</FormLabel>
        <FormGroup>
          {getOptions(field).map((option) => (
            <FormControlLabel
              key={option}
              label={option}
              control={
                <Checkbox
                  checked={selectedValues.includes(option)}
                  onChange={(event) => {
                    onChange(
                      event.target.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter((item) => item !== option),
                    );
                  }}
                />
              }
            />
          ))}
        </FormGroup>
        {field.HelpText ? (
          <Typography variant="caption" color="text.secondary">
            {field.HelpText}
          </Typography>
        ) : null}
      </FormControl>
    );
  }

  const inputTypes = {
    number: "number",
    email: "email",
    phone: "tel",
    date: "date",
    time: "time",
    datetime: "datetime-local",
    file: "url",
  };
  const isDateField = ["date", "time", "datetime"].includes(field.FieldType);

  return (
    <TextField
      {...commonProps}
      type={inputTypes[field.FieldType] || "text"}
      placeholder={
        field.Placeholder ||
        (field.FieldType === "file" ? "Paste a public file URL" : "")
      }
      slotProps={isDateField ? { inputLabel: { shrink: true } } : undefined}
    />
  );
}

function DynamicForm() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.user);
  const token = useMemo(
    () => user?.token || user?.accessToken || user?.jwtToken || "",
    [user],
  );
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let active = true;

    async function loadForm() {
      try {
        setLoading(true);
        setError("");
        setRequiresLogin(false);
        const response = await api.getPublicForm(slug, token);
        const loadedForm = response?.data?.form || response?.data?.data?.form;

        if (active) {
          setForm(loadedForm || null);
          setValues(buildInitialValues(loadedForm?.Fields || []));
        }
      } catch (loadError) {
        if (!active) {
          return;
        }

        if (loadError?.response?.status === 401) {
          setRequiresLogin(true);
        } else {
          setError(getErrorMessage(loadError, "Unable to load this form."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadForm();

    return () => {
      active = false;
    };
  }, [slug, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    const missingCheckbox = (form?.Fields || []).find(
      (field) =>
        field.FieldType === "checkbox" &&
        field.IsRequired &&
        (!Array.isArray(values[field.FieldKey]) ||
          values[field.FieldKey].length === 0),
    );

    if (missingCheckbox) {
      setError(`${missingCheckbox.Label} is required.`);
      return;
    }

    try {
      setSubmitting(true);
      await api.submitPublicForm(slug, { ResponseData: values }, token);
      setSuccessMessage("Your response has been submitted successfully.");
      setValues(buildInitialValues(form?.Fields || []));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (submitError) {
      if (submitError?.response?.status === 401) {
        setRequiresLogin(true);
      } else {
        setError(
          getErrorMessage(submitError, "Unable to submit your response."),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async () => {
    if (isAuthenticated) {
      dispatch(logoutUser());
      await persistor.purge();
    }

    navigate("/login", { state: { from: location.pathname } });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "70vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (requiresLogin) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Paper elevation={0} sx={{ p: 5, borderRadius: 4, textAlign: "center" }}>
          <LockIcon sx={{ fontSize: 48, color: "#1d4ed8", mb: 2 }} />
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Sign in to open this form
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            The administrator has limited this form to authenticated alumni.
          </Typography>
          <Button
            variant="contained"
            onClick={handleLogin}
          >
            {isAuthenticated ? "Sign in again" : "Go to login"}
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!form) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error || "Form not found."}</Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 4, md: 7 },
        background:
          "linear-gradient(180deg, #eff6ff 0%, #f8fafc 42%, #ffffff 100%)",
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: 4,
            border: "1px solid rgba(148, 163, 184, 0.22)",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.09)",
          }}
        >
          <Box
            sx={{
              p: { xs: 3, sm: 5 },
              color: "white",
              background:
                "linear-gradient(135deg, #0f172a 0%, #1d4ed8 65%, #38bdf8 100%)",
            }}
          >
            <Typography variant="overline" sx={{ letterSpacing: 2 }}>
              Einstein Seeds
            </Typography>
            <Typography variant="h3" fontWeight={800} sx={{ mt: 0.5 }}>
              {form.Name}
            </Typography>
            {form.Description ? (
              <Typography sx={{ mt: 1.5, color: "rgba(255,255,255,0.85)" }}>
                {form.Description}
              </Typography>
            ) : null}
          </Box>

          <Stack
            component="form"
            onSubmit={handleSubmit}
            spacing={3}
            sx={{ p: { xs: 3, sm: 5 } }}
          >
            {error ? <Alert severity="error">{error}</Alert> : null}
            {successMessage ? (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                {successMessage}
              </Alert>
            ) : null}

            {(form.Fields || []).map((field) => (
              <DynamicFormField
                key={field.Id || field.FieldKey}
                field={field}
                value={values[field.FieldKey]}
                onChange={(nextValue) =>
                  setValues((currentValues) => ({
                    ...currentValues,
                    [field.FieldKey]: nextValue,
                  }))
                }
              />
            ))}

            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={
                submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />
              }
              disabled={submitting}
              sx={{ alignSelf: "flex-start", px: 4, borderRadius: 2 }}
            >
              {submitting ? "Submitting..." : "Submit response"}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default DynamicForm;
